import { FLANGE, SCULPT } from "@/config/designer";

/**
 * The sculpting substrate: a uniform grid of signed distances in millimetres.
 *
 * Negative = inside the foam, positive = outside. The blank is seeded with the
 * exact analytic signed distance of the block, so an untouched region is
 * mathematically a box — which is what lets the mesher restore perfectly sharp
 * blank edges (see `surfaceNets`).
 *
 * The grid is offset by half a voxel so that no sample ever lands exactly on
 * the mounting plane y = 0. Crossings on the vertical edges of the bottom cell
 * layer therefore interpolate to exactly y = 0, and the rear face comes out
 * dead flat before any snapping is applied.
 */
export class VoxelField {
  /** Sample counts per axis. */
  readonly nx: number;
  readonly ny: number;
  readonly nz: number;
  /** Voxel size, mm. */
  readonly h: number;
  /** World position of sample (0,0,0), mm. */
  readonly ox: number;
  readonly oy: number;
  readonly oz: number;
  /** Block dimensions, mm. */
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  /** Empty sample layers above and below the block. */
  readonly pad: number;
  /** Horizontal workspace reserved for a flange outside the starting blank. */
  readonly horizontalPad: number;

  readonly data: Float32Array;
  /** 1 once a brush has modified the sample; a clean region is still an exact box. */
  readonly dirty: Uint8Array;

  /** Field values are clamped to +/- this, mm. */
  readonly clamp: number;

  constructor(width: number, height: number, depth: number, voxelSize: number, pad = 2) {
    this.width = width;
    this.height = height;
    this.depth = depth;
    this.h = voxelSize;
    this.pad = pad;

    const flangeWorkspace = Math.min(FLANGE.maxWidth, Math.min(width, depth) * 0.4);
    this.horizontalPad = Math.max(pad, Math.ceil(flangeWorkspace / voxelSize) + 2);

    this.nx = Math.ceil(width / voxelSize) + 2 * this.horizontalPad + 1;
    this.ny = Math.ceil(height / voxelSize) + 2 * pad + 1;
    this.nz = Math.ceil(depth / voxelSize) + 2 * this.horizontalPad + 1;

    const horizontalOff = -voxelSize * (this.horizontalPad + 0.5);
    const verticalOff = -voxelSize * (pad + 0.5);
    this.ox = horizontalOff;
    this.oy = verticalOff;
    this.oz = horizontalOff;

    this.clamp = SCULPT.fieldClampVoxels * voxelSize;

    const count = this.nx * this.ny * this.nz;
    this.data = new Float32Array(count);
    this.dirty = new Uint8Array(count);
    this.resetToBlank();
  }

  get sampleCount(): number {
    return this.data.length;
  }

  get minX(): number {
    return this.sampleX(0);
  }

  get maxX(): number {
    return this.sampleX(this.nx - 1);
  }

  get minZ(): number {
    return this.sampleZ(0);
  }

  get maxZ(): number {
    return this.sampleZ(this.nz - 1);
  }

  /** Cell layer (min-corner j index) that contains the mounting plane y = 0. */
  get baseCellJ(): number {
    return this.pad;
  }

  index(i: number, j: number, k: number): number {
    return i + this.nx * (j + this.ny * k);
  }

  sampleX(i: number): number {
    return this.ox + i * this.h;
  }
  sampleY(j: number): number {
    return this.oy + j * this.h;
  }
  sampleZ(k: number): number {
    return this.oz + k * this.h;
  }

  /** Exact signed distance to the untouched blank, mm. */
  blankDistance(x: number, y: number, z: number): number {
    const qx = Math.abs(x - this.width * 0.5) - this.width * 0.5;
    const qy = Math.abs(y - this.height * 0.5) - this.height * 0.5;
    const qz = Math.abs(z - this.depth * 0.5) - this.depth * 0.5;
    const ax = Math.max(qx, 0);
    const ay = Math.max(qy, 0);
    const az = Math.max(qz, 0);
    const outside = Math.sqrt(ax * ax + ay * ay + az * az);
    const inside = Math.min(Math.max(qx, Math.max(qy, qz)), 0);
    return outside + inside;
  }

  clampValue(d: number): number {
    return d < -this.clamp ? -this.clamp : d > this.clamp ? this.clamp : d;
  }

  /** Restore the pristine foam blank. */
  resetToBlank(): void {
    const { nx, ny, nz, data, dirty } = this;
    let p = 0;
    for (let k = 0; k < nz; k++) {
      const z = this.sampleZ(k);
      for (let j = 0; j < ny; j++) {
        const y = this.sampleY(j);
        for (let i = 0; i < nx; i++, p++) {
          data[p] = this.clampValue(this.blankDistance(this.sampleX(i), y, z));
        }
      }
    }
    dirty.fill(0);
  }

  /** Clamped, in-range sample read. Reads outside the grid return "outside". */
  at(i: number, j: number, k: number): number {
    if (i < 0 || j < 0 || k < 0 || i >= this.nx || j >= this.ny || k >= this.nz) {
      return this.clamp;
    }
    return this.data[this.index(i, j, k)] as number;
  }

  /** Trilinearly interpolated field value at a world position, mm. */
  sample(x: number, y: number, z: number): number {
    const fx = (x - this.ox) / this.h;
    const fy = (y - this.oy) / this.h;
    const fz = (z - this.oz) / this.h;
    const i = Math.floor(fx);
    const j = Math.floor(fy);
    const k = Math.floor(fz);
    const tx = fx - i;
    const ty = fy - j;
    const tz = fz - k;

    const c000 = this.at(i, j, k);
    const c100 = this.at(i + 1, j, k);
    const c010 = this.at(i, j + 1, k);
    const c110 = this.at(i + 1, j + 1, k);
    const c001 = this.at(i, j, k + 1);
    const c101 = this.at(i + 1, j, k + 1);
    const c011 = this.at(i, j + 1, k + 1);
    const c111 = this.at(i + 1, j + 1, k + 1);

    const x00 = c000 + (c100 - c000) * tx;
    const x10 = c010 + (c110 - c010) * tx;
    const x01 = c001 + (c101 - c001) * tx;
    const x11 = c011 + (c111 - c011) * tx;
    const y0 = x00 + (x10 - x00) * ty;
    const y1 = x01 + (x11 - x01) * ty;
    return y0 + (y1 - y0) * tz;
  }

  /** Normalised outward gradient at a world position. */
  gradient(
    x: number,
    y: number,
    z: number,
    out: [number, number, number],
  ): [number, number, number] {
    const e = this.h * 0.75;
    const gx = this.sample(x + e, y, z) - this.sample(x - e, y, z);
    const gy = this.sample(x, y + e, z) - this.sample(x, y - e, z);
    const gz = this.sample(x, y, z + e) - this.sample(x, y, z - e);
    const len = Math.hypot(gx, gy, gz) || 1;
    out[0] = gx / len;
    out[1] = gy / len;
    out[2] = gz / len;
    return out;
  }

  /**
   * Highest solid point in the column through (x, z), or null if the column is
   * empty. Used to sit fixing holes on the sculpted surface.
   */
  surfaceHeightAt(x: number, z: number): number | null {
    const step = this.h * 0.5;
    let prevY = this.height + this.h * 2;
    let prev = this.sample(x, prevY, z);
    for (let y = prevY - step; y >= -this.h; y -= step) {
      const v = this.sample(x, y, z);
      if (v < 0 && prev >= 0) {
        const t = prev / (prev - v || 1e-9);
        return prevY + (y - prevY) * t;
      }
      prev = v;
      prevY = y;
    }
    return null;
  }

  /** Solid material remaining in the column through (x, z), mm. */
  columnThicknessAt(x: number, z: number): number {
    const step = this.h * 0.5;
    let solid = 0;
    for (let y = step * 0.5; y <= this.height; y += step) {
      if (this.sample(x, y, z) < 0) solid += step;
    }
    return solid;
  }

  /** Approximate solid volume, mm^3 — used by the diagnostics overlay. */
  approximateVolume(): number {
    const { nx, ny, nz, data } = this;
    const cell = this.h * this.h * this.h;
    let n = 0;
    for (let k = 0; k < nz; k++) {
      for (let j = 0; j < ny; j++) {
        const row = this.index(0, j, k);
        for (let i = 0; i < nx; i++) {
          if ((data[row + i] as number) < 0) n++;
        }
      }
    }
    return n * cell;
  }

  /**
   * March a ray through the field and return the first surface hit.
   * Cheaper and more robust than raycasting the display mesh, and it hits the
   * exact field the brush is about to edit.
   */
  raycast(
    origin: readonly [number, number, number],
    dir: readonly [number, number, number],
    maxDistance = 100_000,
  ): {
    point: [number, number, number];
    normal: [number, number, number];
    distance: number;
  } | null {
    const slab = this.rayBoxRange(origin, dir);
    if (!slab) return null;

    const step = this.h * 0.45;
    let t = Math.max(slab[0], 0);
    const tEnd = Math.min(slab[1], maxDistance);
    if (t > tEnd) return null;

    const valueAt = (d: number): number =>
      this.sample(origin[0] + dir[0] * d, origin[1] + dir[1] * d, origin[2] + dir[2] * d);

    if (valueAt(t) < 0) return null; // started inside — nothing sensible to pick
    let prevT = t;

    for (t += step; t <= tEnd; t += step) {
      if (valueAt(t) < 0) {
        let lo = prevT;
        let hi = t;
        for (let n = 0; n < 14; n++) {
          const mid = (lo + hi) * 0.5;
          if (valueAt(mid) < 0) hi = mid;
          else lo = mid;
        }
        const hitT = (lo + hi) * 0.5;
        const point: [number, number, number] = [
          origin[0] + dir[0] * hitT,
          origin[1] + dir[1] * hitT,
          origin[2] + dir[2] * hitT,
        ];
        const normal = this.gradient(point[0], point[1], point[2], [0, 1, 0]);
        return { point, normal, distance: hitT };
      }
      prevT = t;
    }
    return null;
  }

  /** Ray/AABB overlap for the padded grid bounds. */
  private rayBoxRange(
    origin: readonly [number, number, number],
    dir: readonly [number, number, number],
  ): [number, number] | null {
    const min = [this.ox, this.oy, this.oz];
    const max = [
      this.ox + (this.nx - 1) * this.h,
      this.oy + (this.ny - 1) * this.h,
      this.oz + (this.nz - 1) * this.h,
    ];
    let tMin = -Infinity;
    let tMax = Infinity;
    for (let a = 0; a < 3; a++) {
      const o = origin[a] as number;
      const d = dir[a] as number;
      const lo = min[a] as number;
      const hi = max[a] as number;
      if (Math.abs(d) < 1e-12) {
        if (o < lo || o > hi) return null;
        continue;
      }
      let t0 = (lo - o) / d;
      let t1 = (hi - o) / d;
      if (t0 > t1) {
        const swap = t0;
        t0 = t1;
        t1 = swap;
      }
      if (t0 > tMin) tMin = t0;
      if (t1 < tMax) tMax = t1;
      if (tMin > tMax) return null;
    }
    return [tMin, tMax];
  }
}
